import React, { useEffect, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import StatusToggle from './StatusToggle';
import ShopForm from './ShopForm';
import Loading from '../common/Loading';
import { Settings, X, BellOff, Store, MapPin, Clock, Users, Phone, Tag, Trash2, AlertTriangle, Eye, EyeOff, ShieldAlert, Lightbulb, CheckCheck, ChevronDown, ChevronUp, ShoppingBag, Crown, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShopPack, formatPackExpiry } from '../../hooks/useShopPack';
import PackModal from './PackModal';

/* ─────────────────────────────────────────────────────────────────
   BADGE APPROBATION — affiché dans le hero dashboard
───────────────────────────────────────────────────────────────── */
const APPROVAL_CONFIG = {
  approved: { label: 'Approuvé',    bg: 'linear-gradient(135deg,#00c9a7,#009e85)', icon: '✓' },
  pending:  { label: 'En attente',  bg: 'linear-gradient(135deg,#f5a623,#d97706)', icon: '⏳' },
  rejected: { label: 'Rejeté',      bg: 'linear-gradient(135deg,#f0564a,#dc2626)', icon: '✗' },
};

const ApprovalBadge = ({ approval, reason }) => {
  const cfg = APPROVAL_CONFIG[approval] || APPROVAL_CONFIG.pending;
  return (
    <div style={{ marginTop: '.5rem' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '.3rem',
        padding: '.22rem .65rem', borderRadius: 'var(--r-full)',
        background: cfg.bg, color: '#fff',
        fontSize: '.70rem', fontWeight: 800, letterSpacing: '.2px',
      }}>
        {cfg.icon} {cfg.label}
      </span>
      {approval === 'rejected' && reason && (
        <div style={{
          marginTop: '.4rem', padding: '.5rem .7rem',
          borderRadius: 'var(--r-md)',
          background: 'rgba(240,86,74,.12)',
          border: '1px solid rgba(240,86,74,.25)',
          fontSize: '.75rem', color: 'rgba(255,255,255,.85)', lineHeight: 1.45,
        }}>
          <strong style={{ color: '#fca5a5' }}>Raison : </strong>{reason}
        </div>
      )}
      {(!approval || approval === 'pending') && (
        <div style={{
          marginTop: '.4rem',
          fontSize: '.72rem', color: 'rgba(255,255,255,.55)', lineHeight: 1.4,
        }}>
          Votre magasin sera visible dès validation par l'équipe Yeldi.
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   BADGE STATUT INLINE — compact pour le hero
───────────────────────────────────────────────────────────────── */
const STATUS_LABELS = {
  open:      'Ouvert',
  closed:    'Fermé',
  uncertain: 'Incertain',
  be_back:   'De retour bientôt',
};

const HeroBadge = ({ status }) => (
  <span className={`dash-hero__status-badge ${status || 'closed'}`}>
    <span className="dot" />
    {STATUS_LABELS[status] || 'Fermé'}
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   CARTE MÉTA
───────────────────────────────────────────────────────────────── */
const MetaCard = ({ icon: Icon, label, value, full = false }) => (
  <div className={`dash-hero__meta-card${full ? ' dash-hero__meta-card--full' : ''}`}>
    <div className="dash-hero__meta-icon">
      <Icon size={12} strokeWidth={2.2} />
    </div>
    <div className="dash-hero__meta-body">
      <span className="dash-hero__meta-label">{label}</span>
      <span className="dash-hero__meta-value">{value}</span>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   GUIDE STEPS
───────────────────────────────────────────────────────────────── */
const STEPS = [
  { icon: Clock,   text: 'Appuyez sur le bouton pour ouvrir ou fermer votre magasin.' },
  { icon: Users,   text: 'Vos clients voient le statut en temps réel sur l\'application.' },
  { icon: MapPin,  text: 'Le statut devient "incertain" si vous quittez sans fermer.' },
  { icon: BellOff, text: 'Activez les notifications pour recevoir des rappels automatiques.' },
];

/* ─────────────────────────────────────────────────────────────────
   GUIDE PREMIÈRE FOIS — affiché seulement si jamais fermé
───────────────────────────────────────────────────────────────── */
const GUIDE_KEY = 'yeldi_merchant_guide_done';

const MerchantFirstTimeGuide = () => {
  const [visible, setVisible] = React.useState(() => !localStorage.getItem(GUIDE_KEY));
  const [expanded, setExpanded] = React.useState(true);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(GUIDE_KEY, '1');
    setVisible(false);
  };

  const TIPS = [
    {
      icon: Clock,
      color: '#00c9a7',
      bg:    'rgba(0,201,167,0.10)',
      title: 'Ouvrir / Fermer',
      desc:  'Appuyez sur le grand bouton pour changer votre statut. Vos clients voient le changement en temps réel.',
    },
    {
      icon: MapPin,
      color: '#3b82f6',
      bg:    'rgba(59,130,246,0.10)',
      title: 'Statut incertain',
      desc:  'Si vous quittez le magasin sans appuyer sur "Fermer", votre statut devient "Incertain" au bout de quelques minutes.',
    },
    {
      icon: Clock,
      color: '#f5a623',
      bg:    'rgba(245,166,35,0.10)',
      title: 'Absence temporaire',
      desc:  'Vous partez 10 min ? Utilisez le bouton "Je reviens dans…" pour prévenir vos clients.',
    },
    {
      icon: Users,
      color: '#8b5cf6',
      bg:    'rgba(139,92,246,0.10)',
      title: 'Vos clients',
      desc:  'Ils voient votre statut en direct depuis leur téléphone avant de se déplacer.',
    },
  ];

  return (
    <div style={{
      margin: '1rem 1rem 0',
      borderRadius: 'var(--md-radius-xl)',
      border: '1.5px solid rgba(0,201,167,0.25)',
      background: 'var(--md-surface)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '0.6rem', padding: '0.85rem 1rem',
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit',
          borderBottom: expanded ? '1px solid var(--md-outline)' : 'none',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 'var(--md-radius-sm)',
          background: 'rgba(0,201,167,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Lightbulb size={16} color="#00c9a7" strokeWidth={2}/>
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--md-text-primary)' }}>
            Guide de démarrage
          </div>
          <div style={{ fontSize: '0.71rem', color: 'var(--md-text-secondary)', marginTop: '0.05rem' }}>
            Tout ce qu'il faut savoir pour débuter
          </div>
        </div>
        <div style={{ color: 'var(--md-text-secondary)', flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </div>
      </button>

      {/* Contenu dépliable */}
      {expanded && (
        <div style={{ padding: '0.75rem 1rem 1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.9rem' }}>
            {TIPS.map(({ icon: Icon, color, bg, title, desc }, i) => (
              <div key={i} style={{
                display: 'flex', gap: '0.65rem', alignItems: 'flex-start',
                padding: '0.65rem 0.75rem',
                borderRadius: 'var(--md-radius-lg)',
                background: 'var(--md-surface-2)',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 'var(--md-radius-sm)',
                  background: bg, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: '0.05rem',
                }}>
                  <Icon size={15} color={color} strokeWidth={2.2}/>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--md-text-primary)', marginBottom: '0.15rem' }}>
                    {title}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--md-text-secondary)', lineHeight: 1.5 }}>
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bouton "J'ai compris" */}
          <button
            onClick={dismiss}
            style={{
              width: '100%', padding: '0.7rem',
              borderRadius: 'var(--md-radius-lg)',
              border: '1.5px solid rgba(0,201,167,0.30)',
              background: 'rgba(0,201,167,0.10)',
              color: '#00c9a7', fontWeight: 700,
              fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.4rem',
            }}
          >
            <CheckCheck size={16} strokeWidth={2.5}/>
            J'ai compris — ne plus afficher
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SECTION PACK + BOUTIQUE
───────────────────────────────────────────────────────────────── */
const ShopPackSection = ({ shop, onManagePack, onGotoStore }) => {
  const { pack, isExpired, expiry } = useShopPack(shop);
  const expiryLabel = expiry ? formatPackExpiry(expiry) : null;

  return (
    <div style={{ margin: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

      {/* Bouton Gérer la boutique */}
      <button
        onClick={onGotoStore}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.85rem 1rem',
          borderRadius: 'var(--md-radius-xl)',
          border: '1.5px solid rgba(0,201,167,0.25)',
          background: 'linear-gradient(135deg, rgba(0,201,167,0.06), rgba(27,42,107,0.06))',
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'all 0.15s',
          width: '100%',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 'var(--md-radius-sm)',
          background: 'rgba(0,201,167,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <ShoppingBag size={18} color="#00c9a7" strokeWidth={2}/>
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--md-text-primary)' }}>
            Gérer ma boutique
          </div>
          <div style={{ fontSize: '0.70rem', color: 'var(--md-text-secondary)', marginTop: '0.1rem' }}>
            Produits, catégories, catalogue en ligne
          </div>
        </div>
        <ChevronUp size={16} color="var(--md-text-secondary)" style={{ transform: 'rotate(90deg)', flexShrink: 0 }}/>
      </button>

      {/* Badge pack actuel */}
      <button
        onClick={onManagePack}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.65rem 0.85rem',
          borderRadius: 'var(--md-radius-lg)',
          border: `1.5px solid ${isExpired ? 'rgba(239,68,68,0.25)' : pack.color + '30'}`,
          background: isExpired ? 'rgba(239,68,68,0.06)' : pack.color + '0a',
          cursor: 'pointer', fontFamily: 'inherit', width: '100%',
        }}
      >
        <Crown size={14} color={isExpired ? '#ef4444' : pack.color} style={{ flexShrink: 0 }}/>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <span style={{ fontWeight: 700, fontSize: '0.78rem', color: isExpired ? '#ef4444' : pack.color }}>
            Pack {pack.name}
          </span>
          {expiryLabel && (
            <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: isExpired ? '#ef4444' : 'var(--md-text-disabled)', fontWeight: 500 }}>
              · {expiryLabel}
            </span>
          )}
        </div>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: pack.color, background: pack.color + '18', borderRadius: '999px', padding: '0.15rem 0.55rem', flexShrink: 0 }}>
          {isExpired ? 'Renouveler' : 'Changer'}
        </span>
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   DASHBOARD
───────────────────────────────────────────────────────────────── */
const Dashboard = () => {
  const { user, deleteAccount } = useAuth();
  const { requestPermission, permissionDenied } = useNotifications();
  const navigate   = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPack, setShowPack] = useState(false);
  const [shop,       setShop]       = useState(null);
  /* Toast feedback — affiché après retour d'une action notification push */
  const [toast, setToast] = useState(null); // { type: 'success'|'info', msg: string }
  const [showDelete, setShowDelete] = useState(false);
  const [deletePwd,  setDeletePwd]  = useState('');
  const [showDelPwd, setShowDelPwd] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [deleteErr,  setDeleteErr]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!user) return;
    requestPermission();

    const sendTokenToSW = async () => {
      if (!navigator.serviceWorker) return;
      const reg = await navigator.serviceWorker.ready;
      reg.active?.postMessage({
        type:      'SET_FIREBASE_PROJECT_ID',
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      });
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) reg.active?.postMessage({ type: 'SET_FIREBASE_ID_TOKEN', token });
      } catch (err) {
        console.warn('Dashboard: impossible d\'envoyer le token au SW', err);
      }
    };
    sendTokenToSW();
    const tokenRefreshInterval = setInterval(sendTokenToSW, 55 * 60 * 1000);

    let unsub = null;
    const init = async () => {
      try {
        const mDoc  = await getDoc(doc(db, 'merchants', user.uid));
        const mData = mDoc.data();
        if (mData?.shopId) {
          unsub = onSnapshot(doc(db, 'shops', mData.shopId), (snap) => {
            if (snap.exists()) setShop({ id: snap.id, ...snap.data() });
            setLoading(false);
          });
        } else {
          setLoading(false);
          setShowForm(true);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    init();
    return () => { if (unsub) unsub(); clearInterval(tokenRefreshInterval); };
  }, [user, requestPermission]);

  /* ── Feedback après action depuis notification push ─────────── */
  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;
    if (action === 'closed') {
      setToast({ type: 'success', msg: 'Magasin fermé avec succès depuis la notification.' });
    } else if (action === 'kept-open') {
      setToast({ type: 'info', msg: 'Statut maintenu ouvert.' });
    }
    /* Retirer le paramètre de l'URL sans recharger */
    setSearchParams({}, { replace: true });
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [searchParams, setSearchParams]);

  /* ── Suppression du compte ── */
  const handleDelete = async () => {
    if (!deletePwd.trim()) { setDeleteErr('Veuillez saisir votre mot de passe.'); return; }
    setDeleting(true); setDeleteErr('');
    try {
      await deleteAccount(deletePwd);
      // deleteAccount supprime le compte → onAuthStateChanged redirigera vers /
    } catch (err) {
      const msg =
        err.code === 'auth/wrong-password'  ? 'Mot de passe incorrect.' :
        err.code === 'auth/too-many-requests' ? 'Trop de tentatives. Réessayez plus tard.' :
        err.code === 'auth/requires-recent-login' ? 'Session expirée. Reconnectez-vous.' :
        'Erreur : ' + (err.message || 'Impossible de supprimer le compte.');
      setDeleteErr(msg);
      setDeleting(false);
    }
  };

  if (loading) return <Loading />;
  if (!shop && !showForm) return <Loading />;

  /* ── Toast feedback après retour d'une action notification push ── */
  const ToastBanner = toast ? (
    <div style={{
      position: 'fixed', bottom: '5.5rem', left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, display: 'flex', alignItems: 'center', gap: '0.6rem',
      padding: '0.7rem 1.1rem', borderRadius: 'var(--r-full)',
      background: toast.type === 'success' ? '#00c9a7' : '#3b82f6',
      color: '#fff', fontSize: '0.82rem', fontWeight: 700,
      boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
      whiteSpace: 'nowrap',
    }}>
      <CheckCircle2 size={16} strokeWidth={2.5} />
      {toast.msg}
    </div>
  ) : null;

  /* ══════════════════════════════════════════
     VUE FORMULAIRE
  ══════════════════════════════════════════ */
  if (showForm) {
    return (
      <div className="page-enter">
        {ToastBanner}
        <div className="shopform-hero">
          <div className="shopform-hero__topbar">
            <span className="shopform-hero__label">Mon magasin</span>
            {shop && (
              <button
                className="shopform-hero__close"
                onClick={() => setShowForm(false)}
                aria-label="Fermer"
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>

          <div className="shopform-hero__main">
            <div className="shopform-hero__icon">
              <Store size={18} strokeWidth={2} />
            </div>
            <h2 className="shopform-hero__title">
              {shop ? 'Modifier mon magasin' : 'Créer mon magasin'}
            </h2>
          </div>

          <p className="shopform-hero__sub">
            {shop ? 'Mettez à jour vos informations' : 'Remplissez les informations ci-dessous'}
          </p>
        </div>

        <div style={{ padding: '1rem' }}>
          <ShopForm shop={shop} onSuccess={(s) => { setShop(s); setShowForm(false); }} />
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════
     DASHBOARD PRINCIPAL
  ══════════════════════════════════════════ */

  /* Méta-infos disponibles */
  const hasMeta = shop.address || shop.commerceType || shop.phone;
  /* Adresse seule → pleine largeur ; sinon grille 2 col */
  const addressOnly = shop.address && !shop.commerceType && !shop.phone;

  return (
    <div className="page-enter" style={{ paddingBottom: '1rem' }}>
      {ToastBanner}

      {/* ══════ HERO ══════ */}
      <div className="dash-hero">

        {/* Sur-titre + bouton modifier en haut à droite */}
        <div className="dash-hero__topbar">
          <span className="dash-hero__label">Mon magasin</span>
          <button
            className="dash-hero__edit-btn"
            onClick={() => setShowForm(true)}
            aria-label="Modifier le magasin"
          >
            <Settings size={12} strokeWidth={2.2} />
            Modifier
          </button>
        </div>

        {/* Icône + colonne [nom + badge] */}
        <div className="dash-hero__main">
          <div className="dash-hero__icon">
            <Store size={20} strokeWidth={1.8} />
          </div>

          <div className="dash-hero__info">

            {/* Nom + badge statut sur la même ligne */}
            <div className="dash-hero__name-row">
              <h2 className="dash-hero__name">{shop.name}</h2>
              <HeroBadge status={shop.status} />
            </div>

            {/* Badge approbation */}
            <ApprovalBadge approval={shop.approval} reason={shop.rejectionReason} />

          </div>
        </div>

        {/* Méta-infos en grille de cartes */}
        {hasMeta && (
          <div className="dash-hero__meta">

            {/* Adresse — pleine largeur si seule, sinon normale */}
            {shop.address && (
              <MetaCard
                icon={MapPin}
                label="Adresse"
                value={shop.address}
                full={addressOnly}
              />
            )}

            {/* Type de commerce */}
            {shop.commerceType && (
              <MetaCard
                icon={Tag}
                label="Type"
                value={shop.commerceType.replace('_', ' ')}
              />
            )}

            {/* Téléphone */}
            {shop.phone && (
              <MetaCard
                icon={Phone}
                label="Téléphone"
                value={shop.phone}
              />
            )}

          </div>
        )}
      </div>

      {/* ══════ ALERTE NOTIFICATIONS ══════ */}
      {permissionDenied && (
        <div className="dash-notif-alert">
          <BellOff size={17} color="var(--warning)" style={{ flexShrink: 0, marginTop: 1 }} />
          <div className="dash-notif-alert__text">
            <span className="dash-notif-alert__title">Notifications bloquées</span>
            Les rappels automatiques sont désactivés. Autorisez-les dans les réglages de votre navigateur.
          </div>
        </div>
      )}

      {/* ══════ PACK + BOUTIQUE ══════ */}
      <ShopPackSection shop={shop} onManagePack={() => setShowPack(true)} onGotoStore={() => navigate('/merchant/store')} />

      {/* ══════ GUIDE PREMIÈRE FOIS ══════ */}
      <MerchantFirstTimeGuide />

      {/* ══════ TOGGLE STATUT ══════ */}
      <div style={{ margin: '1rem 0 0' }}>
        <StatusToggle shop={shop} />
      </div>

      {/* ══════ GUIDE RAPIDE ══════ */}
      <div className="dash-guide">

        <div className="dash-guide__header">
          <div className="dash-guide__icon-wrap">
            <Users size={15} strokeWidth={2} color="var(--primary)" />
          </div>
          <span className="dash-guide__title">Comment ça marche ?</span>
        </div>

        <div className="dash-guide__body">
          {STEPS.map(({ icon: Icon, text }, i) => (
            <div key={i} className="dash-step">
              <div className="dash-step__num">{i + 1}</div>
              <Icon size={14} strokeWidth={2} className="dash-step__icon" />
              <p className="dash-step__text">{text}</p>
            </div>
          ))}
        </div>

      </div>

      {/* ══════ ZONE DANGER ══════ */}
      <div className="dash-danger-zone">

        <div className="dash-danger-zone__header">
          <ShieldAlert size={13} color="var(--error)" />
          <span className="dash-danger-zone__label">Zone de danger</span>
        </div>

        <button
          className="dash-danger-btn"
          onClick={() => { setShowDelete(true); setDeleteErr(''); setDeletePwd(''); }}
        >
          <div className="dash-danger-btn__icon">
            <Trash2 size={16} strokeWidth={2} />
          </div>
          <div className="dash-danger-btn__text">
            <div className="dash-danger-btn__title">Supprimer mon compte</div>
            <div className="dash-danger-btn__sub">Supprime le magasin et toutes vos données</div>
          </div>
          <AlertTriangle size={15} color="var(--error)" style={{ flexShrink: 0, opacity: .7 }} />
        </button>

      </div>

      {/* ══════ MODAL SUPPRESSION ══════ */}
      {showDelete && (
        <div
          className="delete-overlay"
          onClick={e => { if (e.target === e.currentTarget && !deleting) setShowDelete(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-title"
        >
          <div className="delete-sheet">
            <div className="delete-sheet__handle" />

            {/* Icône */}
            <div className="delete-sheet__icon">
              <Trash2 size={28} strokeWidth={1.8} />
            </div>

            <h2 className="delete-sheet__title" id="delete-title">
              Supprimer mon compte ?
            </h2>

            <p className="delete-sheet__sub">
              Cette action est <strong>irréversible</strong>. Toutes vos données seront définitivement supprimées.
            </p>

            {/* Liste des données supprimées */}
            <div className="delete-sheet__list">
              {[
                'Fiche de votre magasin',
                'Horaires et informations',
                'Votre compte commerçant',
                'Toutes vos données personnelles',
              ].map(item => (
                <div key={item} className="delete-sheet__list-item">
                  <span className="delete-sheet__list-dot" />
                  {item}
                </div>
              ))}
            </div>

            {/* Champ mot de passe */}
            <label className="delete-sheet__pwd-label" htmlFor="del-pwd">
              Confirmez votre mot de passe
            </label>
            <div className="delete-sheet__pwd-wrap">
              <input
                id="del-pwd"
                type={showDelPwd ? 'text' : 'password'}
                className="delete-sheet__pwd-input"
                placeholder="Votre mot de passe actuel"
                value={deletePwd}
                onChange={e => { setDeletePwd(e.target.value); setDeleteErr(''); }}
                autoComplete="current-password"
                disabled={deleting}
              />
              <button
                type="button"
                className="delete-sheet__pwd-toggle"
                onClick={() => setShowDelPwd(v => !v)}
                aria-label={showDelPwd ? 'Masquer' : 'Afficher'}
              >
                {showDelPwd ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            {/* Erreur */}
            {deleteErr && (
              <div className="alert alert-error" style={{ margin: '0 0 1rem', fontSize: '.82rem' }}>
                <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                <span>{deleteErr}</span>
              </div>
            )}

            {/* Actions */}
            <button
              className="delete-sheet__confirm"
              onClick={handleDelete}
              disabled={deleting || !deletePwd.trim()}
            >
              <Trash2 size={17} strokeWidth={2} />
              {deleting ? 'Suppression en cours…' : 'Supprimer définitivement'}
            </button>
            <button
              className="delete-sheet__cancel"
              onClick={() => setShowDelete(false)}
              disabled={deleting}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ══════ PACK MODAL ══════ */}
      {showPack && <PackModal shop={shop} onClose={() => setShowPack(false)} />}

    </div>
  );
};

export default Dashboard;